import React from 'react';
import { ChevronDown, ChevronRight, Download } from 'lucide-react';
import { InlineLoadingState } from '../../../../components/LoadingSpinner';
import { SettingsGroup } from '../integrations/integrationUi';
import { OPENAPI_SPEC_PATH } from '../../../../config/publicApi';
import { SETTINGS_BTN_OUTLINE } from '../../utils/settingsSectionStyles';
import { MethodBadge } from './ApiQuickstart';

/**
 * Schlanke Referenz aus der OpenAPI-Spec des Servers, im Stil der Einstellungen gerendert.
 * Bewusst kein externer Viewer: kein Fremd-Script auf der eingeloggten Origin, kein „Try it“
 * mit Server-Schlüsseln im Browser.
 */

type Json = Record<string, unknown>;

type Operation = {
  method: string;
  path: string;
  summary: string;
  description?: string;
  scope: string;
  deprecated: boolean;
  tag: string;
  parameters: Json[];
  requestBody?: Json;
  responses: Record<string, Json>;
};

const METHODS = ['get', 'post', 'patch', 'put', 'delete'] as const;

function resolveRef(spec: Json, node: unknown, depth = 0): Json {
  if (!node || typeof node !== 'object' || depth > 8) return (node ?? {}) as Json;
  const ref = (node as Json).$ref;
  if (typeof ref !== 'string' || !ref.startsWith('#/')) return node as Json;
  const target = ref
    .slice(2)
    .split('/')
    .reduce<unknown>((acc, key) => (acc && typeof acc === 'object' ? (acc as Json)[key] : undefined), spec);
  return resolveRef(spec, target, depth + 1);
}

function collectOperations(spec: Json): Operation[] {
  const paths = (spec.paths ?? {}) as Record<string, Json>;
  const out: Operation[] = [];
  for (const [path, item] of Object.entries(paths)) {
    const shared = Array.isArray(item.parameters) ? (item.parameters as Json[]) : [];
    for (const method of METHODS) {
      const op = item[method] as Json | undefined;
      if (!op) continue;
      const own = Array.isArray(op.parameters) ? (op.parameters as Json[]) : [];
      out.push({
        method: method.toUpperCase(),
        path,
        summary: String(op.summary ?? ''),
        description: typeof op.description === 'string' ? op.description : undefined,
        scope: String(op['x-required-scope'] ?? ''),
        deprecated: op.deprecated === true,
        tag: Array.isArray(op.tags) && op.tags.length > 0 ? String(op.tags[0]) : 'Sonstiges',
        parameters: [...shared, ...own].map((p) => resolveRef(spec, p)),
        requestBody: op.requestBody ? resolveRef(spec, op.requestBody) : undefined,
        responses: (op.responses ?? {}) as Record<string, Json>,
      });
    }
  }
  return out;
}

function typeLabel(schema: Json | undefined): string {
  if (!schema) return '';
  const t = schema.type;
  if (Array.isArray(t)) return t.filter((x) => x !== 'null').join(' | ') + (t.includes('null') ? ' | null' : '');
  if (typeof t === 'string') {
    if (t === 'array') return `${typeLabel((schema.items ?? {}) as Json)}[]`;
    if (schema.format) return `${t} (${String(schema.format)})`;
    if (Array.isArray(schema.enum)) return schema.enum.map(String).join(' | ');
    return t;
  }
  if (Array.isArray(schema.oneOf)) return 'oneOf';
  if (Array.isArray(schema.allOf)) return 'object';
  return '';
}

function schemaProperties(spec: Json, schema: Json | undefined, depth = 0): Array<{ name: string; type: string; required: boolean; description: string }> {
  if (!schema || depth > 3) return [];
  const resolved = resolveRef(spec, schema);
  if (Array.isArray(resolved.allOf)) {
    return (resolved.allOf as Json[]).flatMap((part) => schemaProperties(spec, part, depth + 1));
  }
  if (Array.isArray(resolved.oneOf)) {
    const first = (resolved.oneOf as Json[]).find((part) => resolveRef(spec, part).type !== 'array') ?? (resolved.oneOf as Json[])[0];
    return schemaProperties(spec, first, depth + 1);
  }
  if (resolved.type === 'array') return schemaProperties(spec, resolved.items as Json, depth + 1);
  const props = (resolved.properties ?? {}) as Record<string, Json>;
  const required = new Set(Array.isArray(resolved.required) ? resolved.required.map(String) : []);
  return Object.entries(props).map(([name, raw]) => {
    const prop = resolveRef(spec, raw);
    return {
      name,
      type: typeLabel(prop),
      required: required.has(name),
      description: typeof prop.description === 'string' ? prop.description : '',
    };
  });
}

export function ApiReference() {
  const [spec, setSpec] = React.useState<Json | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [open, setOpen] = React.useState<string | null>(null);

  React.useEffect(() => {
    let cancelled = false;
    fetch(OPENAPI_SPEC_PATH, { credentials: 'include' })
      .then(async (r) => {
        const data = (await r.json().catch(() => null)) as Json | null;
        if (!r.ok || !data) throw new Error((data?.error as string) || 'Referenz konnte nicht geladen werden.');
        return data;
      })
      .then((data) => {
        if (!cancelled) setSpec(data);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const operations = React.useMemo(() => (spec ? collectOperations(spec) : []), [spec]);
  const tags = React.useMemo(() => {
    const order = Array.isArray(spec?.tags) ? (spec?.tags as Json[]).map((t) => String(t.name)) : [];
    const seen = new Set<string>();
    const result: Array<{ name: string; description?: string; ops: Operation[] }> = [];
    for (const name of [...order, ...operations.map((o) => o.tag)]) {
      if (seen.has(name)) continue;
      seen.add(name);
      const ops = operations.filter((o) => o.tag === name);
      if (ops.length === 0) continue;
      const meta = Array.isArray(spec?.tags) ? (spec?.tags as Json[]).find((t) => String(t.name) === name) : undefined;
      result.push({ name, description: typeof meta?.description === 'string' ? meta.description : undefined, ops });
    }
    return result;
  }, [spec, operations]);

  const downloadSpec = () => {
    if (!spec) return;
    const blob = new Blob([JSON.stringify(spec, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'meisterpfad-api-openapi.json';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <SettingsGroup
      title="Referenz"
      description={
        <>
          Alle Endpunkte mit Feldern, Berechtigungen und Antworten, erzeugt aus der OpenAPI-Spezifikation. Die Spec lässt sich für Postman, Insomnia oder Code-Generatoren herunterladen.
        </>
      }
    >
      <div className="space-y-4 px-5 py-4">
        {error ? (
          <div className="p-3 rounded-lg bg-red-50 text-red-700 text-sm border border-red-200">{error}</div>
        ) : !spec ? (
          <InlineLoadingState label="Lade Referenz…" className="justify-start" />
        ) : (
          <>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="text-xs text-app-text-muted">
                OpenAPI {String(spec.openapi)} · Version {String((spec.info as Json | undefined)?.version ?? '')}
              </p>
              <button type="button" className={`${SETTINGS_BTN_OUTLINE} gap-2`} onClick={downloadSpec}>
                <Download className="w-4 h-4" />
                OpenAPI-Spec (JSON)
              </button>
            </div>
            {tags.map((tag) => (
              <section key={tag.name}>
                <h3 className="text-sm font-semibold text-app-text">{tag.name}</h3>
                {tag.description ? <p className="mt-0.5 text-xs text-app-text-muted">{tag.description}</p> : null}
                <ul className="mt-2 divide-y divide-app-border/60">
                  {tag.ops.map((op) => {
                    const key = `${op.method} ${op.path}`;
                    const expanded = open === key;
                    return (
                      <li key={key}>
                        <button
                          type="button"
                          className="-mx-2 flex w-[calc(100%+1rem)] items-start gap-2 rounded-md px-2 py-2.5 text-left transition-colors hover:bg-app-surface-muted/60"
                          onClick={() => setOpen(expanded ? null : key)}
                          aria-expanded={expanded}
                        >
                          {expanded ? <ChevronDown className="mt-0.5 h-4 w-4 shrink-0 text-app-text-subtle" /> : <ChevronRight className="mt-0.5 h-4 w-4 shrink-0 text-app-text-subtle" />}
                          <MethodBadge method={op.method} />
                          <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                              <span className="text-sm font-medium text-app-text">{op.summary}</span>
                              {op.deprecated ? <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-800">Legacy</span> : null}
                              {op.scope ? <span className="font-mono text-[11px] text-app-text-subtle">{op.scope}</span> : null}
                            </div>
                            <code className="mt-0.5 block break-all font-mono text-xs text-app-text-muted">{op.path}</code>
                          </div>
                        </button>
                        {expanded ? <OperationDetails spec={spec} op={op} /> : null}
                      </li>
                    );
                  })}
                </ul>
              </section>
            ))}
          </>
        )}
      </div>
    </SettingsGroup>
  );
}

function OperationDetails({ spec, op }: { spec: Json; op: Operation }) {
  const bodyContent = op.requestBody ? ((op.requestBody.content ?? {}) as Record<string, Json>) : {};
  const [bodyMime, bodyMedia] = Object.entries(bodyContent)[0] ?? [];
  const bodyProps = bodyMedia ? schemaProperties(spec, bodyMedia.schema as Json) : [];
  const example = bodyMedia?.example;
  const okResponse = Object.entries(op.responses).find(([code]) => code.startsWith('2'));
  const okMedia = okResponse ? (((resolveRef(spec, okResponse[1]).content ?? {}) as Record<string, Json>)['application/json'] as Json | undefined) : undefined;
  const responseProps = okMedia ? schemaProperties(spec, okMedia.schema as Json) : [];

  return (
    <div className="space-y-4 pb-4 pl-6 pt-1 text-sm sm:pl-[5.5rem]">
      {op.description ? <p className="whitespace-pre-line text-app-text-muted">{op.description}</p> : null}

      {op.parameters.length > 0 ? (
        <Section title="Parameter">
          <FieldTable
            rows={op.parameters.map((p) => [
              String(p.name),
              `${String(p.in)}${p.required ? ' · Pflicht' : ''}`,
              [typeLabel(p.schema as Json), typeof p.description === 'string' ? p.description : ''].filter(Boolean).join(' – '),
            ])}
          />
        </Section>
      ) : null}

      {bodyMedia ? (
        <Section title={`Request-Body (${bodyMime})`}>
          {bodyProps.length > 0 ? (
            <FieldTable rows={bodyProps.map((p) => [p.name, `${p.type}${p.required ? ' · Pflicht' : ''}`, p.description])} />
          ) : (
            <p className="text-xs text-app-text-muted">{typeof (bodyMedia.schema as Json)?.description === 'string' ? String((bodyMedia.schema as Json).description) : 'Siehe Spezifikation.'}</p>
          )}
          {example !== undefined ? (
            <pre className="app-scrollbar mt-2 overflow-x-auto rounded-lg bg-slate-900 p-3 font-mono text-xs leading-relaxed text-slate-100">
              <code>{JSON.stringify(example, null, 2)}</code>
            </pre>
          ) : null}
        </Section>
      ) : null}

      <Section title="Antworten">
        <FieldTable
          head={['Status', 'Bedeutung']}
          rows={Object.entries(op.responses).map(([code, raw]) => [code, String(resolveRef(spec, raw).description ?? '')])}
        />
        {responseProps.length > 0 ? (
          <div className="mt-2">
            <p className="mb-1 text-xs font-medium text-app-text-muted">Felder der Erfolgsantwort</p>
            <FieldTable rows={responseProps.map((p) => [p.name, p.type, p.description])} />
          </div>
        ) : null}
      </Section>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <p className="text-xs font-semibold uppercase tracking-wide text-app-text-subtle">{title}</p>
      {children}
    </div>
  );
}

function FieldTable({ rows, head = ['Feld', 'Typ', 'Beschreibung'] }: { rows: string[][]; head?: string[] }) {
  if (rows.length === 0) return null;
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[420px] text-sm">
        <thead>
          <tr className="text-left text-xs font-medium text-app-text-muted">
            {head.map((label, index) => (
              <th key={index} className="py-1.5 pr-3 font-medium">
                {label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={`${row[0]}-${i}`} className="border-t border-app-border/60 align-top">
              <td className="whitespace-nowrap py-1.5 pr-3 font-mono text-xs text-app-text">{row[0]}</td>
              {row.slice(1).map((cell, index) => (
                <td key={index} className={`py-1.5 pr-3 ${row.length === 3 && index === 0 ? 'whitespace-nowrap text-xs text-app-text-muted' : 'text-app-text-muted'}`}>
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
